import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, DownloadSimple, Trash, ArrowRight, Trophy, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const scoreTone = (s) =>
  s >= 70 ? "text-[hsl(var(--success))]"
  : s >= 50 ? "text-[hsl(var(--warning))]"
  : "text-destructive";

export default function HistoryPage() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  // filters
  const [classes, setClasses] = useState([]);
  const [exams, setExams] = useState([]);
  const [classId, setClassId] = useState("");
  const [examId, setExamId] = useState("");

  // ranking view
  const [view, setView] = useState("list"); // list | ranking
  const [ranking, setRanking] = useState(null);
  const [rankLoading, setRankLoading] = useState(false);
  const [bulkReprocessing, setBulkReprocessing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);

  /* ------ Filter data ------ */
  useEffect(() => {
    (async () => {
      try {
        const c = await api.get("/classes");
        setClasses(c.data);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!classId) { setExams([]); setExamId(""); return; }
      try {
        const r = await api.get(`/exams?class_id=${classId}`);
        setExams(r.data);
        if (!r.data.find(e => e.id === examId)) setExamId("");
      } catch { setExams([]); }
    })();
    // eslint-disable-next-line
  }, [classId]);

  /* ------ Data loading ------ */
  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (classId) params.class_id = classId;
      if (examId) params.exam_id = examId;
      const res = await api.get("/omr/corrections", { params });
      setRows(res.data.results || []);
    } catch (e) {
      toast.error("Falha ao carregar histórico");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, classId, examId]);

  /* ------ Ranking ------ */
  useEffect(() => {
    if (view !== "ranking" || !examId) { setRanking(null); return; }
    (async () => {
      setRankLoading(true);
      try {
        const params = new URLSearchParams({ exam_id: examId });
        if (classId) params.set("class_id", classId);
        const r = await api.get(`/omr/ranking?${params}`);
        setRanking(r.data);
      } catch {
        toast.error("Falha ao carregar ranking");
        setRanking(null);
      } finally {
        setRankLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [view, examId, classId]);

  /* ------ Actions ------ */
  const handleClear = async () => {
    try {
      const res = await api.delete("/omr/corrections/all");
      toast.success(`${res.data.deleted} correção(ões) removida(s)`);
      load();
    } catch (e) {
      toast.error("Falha ao limpar histórico");
    }
  };

  const handleClearWithoutExam = async () => {
    try {
      const res = await api.delete("/omr/corrections/orphaned");
      toast.success(`${res.data.deleted || 0} correção(ões) sem prova removida(s)`);
      load();
    } catch (e) {
      toast.error("Falha ao remover correções sem prova");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/omr/corrections/${id}`);
      toast.success("Registro removido");
      setRows((r) => r.filter((x) => x.id !== id));
    } catch {
      toast.error("Falha ao remover");
    }
  };

  const handleReprocessAll = async () => {
    setBulkReprocessing(true);
    setBulkProgress(0);
    try {
      const queued = await api.post("/omr/corrections/reprocess-all", {
        fill_threshold: 0.22,
        dominance: 1.25,
      });
      const { job_id: jobId, total } = queued.data;
      toast.success(`${total} cartão(ões) colocado(s) na fila de reprocessamento`);
      while (true) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const response = await api.get(`/omr/jobs/${jobId}`);
        const job = response.data;
        const denominator = Math.max(Number(job.total_pages || total || 1), 1);
        setBulkProgress(Math.min(100, Math.round((Number(job.processed_pages || 0) / denominator) * 100)));
        if (job.status === "completed") {
          const failures = Number(job.failure_count || 0);
          toast.success(
            `${Number(job.success_count || 0)} cartão(ões) reprocessado(s)${failures ? ` · ${failures} falharam` : ""}`,
          );
          await load();
          break;
        }
        if (job.status === "failed") {
          throw new Error(job.error || "O reprocessamento em massa falhou");
        }
      }
    } catch (e) {
      toast.error("Falha ao reprocessar: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBulkReprocessing(false);
    }
  };

  const exportUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (classId) params.set("class_id", classId);
    if (examId) params.set("exam_id", examId);
    const qs = params.toString();
    return `${API}/omr/export.csv${qs ? "?" + qs : ""}`;
  }, [classId, examId]);

  const withExam = !!examId; // whether score columns should show

  return (
    <div className="max-w-[1400px] mx-auto px-8 py-10">
      <div className="flex items-end justify-between mb-8 border-b border-border pb-6">
        <div>
          <div className="label-eyebrow mb-2">histórico de correções</div>
          <h1 className="font-display text-5xl font-black tracking-tighter leading-none">
            Ledger.
          </h1>
          <p className="text-muted-foreground mt-3 text-sm max-w-[520px]">
            Todos os cartões processados. Filtre por turma/prova para ver notas e ranking; exporte o dataset em CSV.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                disabled={bulkReprocessing}
                data-testid="reprocess-all-btn"
                className="rounded-none border-primary text-primary font-mono-data uppercase tracking-widest text-xs"
              >
                <ArrowClockwise size={14} weight="bold" className={`mr-2 ${bulkReprocessing ? "animate-spin" : ""}`} />
                {bulkReprocessing ? `Reprocessando ${bulkProgress}%` : "Reprocessar todos"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-none">
              <AlertDialogHeader>
                <AlertDialogTitle>Reprocessar todos os cartões?</AlertDialogTitle>
                <AlertDialogDescription>
                  Todos os cartões acessíveis serão relidos pelo motor OMR atual. O processamento será sequencial pela fila e substituirá as leituras anteriores, preservando provas e vínculos.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-none">Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleReprocessAll}
                  data-testid="reprocess-all-confirm"
                  className="rounded-none"
                >
                  Sim, reprocessar todos
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                data-testid="clear-without-exam-btn"
                className="rounded-none border-border text-[hsl(var(--warning))] font-mono-data uppercase tracking-widest text-xs"
              >
                <Trash size={14} weight="bold" className="mr-2" />
                Apagar sem prova
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-none">
              <AlertDialogHeader>
                <AlertDialogTitle>Apagar correções sem prova?</AlertDialogTitle>
                <AlertDialogDescription>
                  Remove permanentemente cartões não vinculados e cartões de provas já apagadas, incluindo seus arquivos.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-none">Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleClearWithoutExam}
                  className="rounded-none bg-destructive text-destructive-foreground"
                >
                  Sim, apagar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                data-testid="clear-history-btn"
                className="rounded-none border-border text-destructive hover:bg-destructive hover:text-destructive-foreground font-mono-data uppercase tracking-widest text-xs"
              >
                <Trash size={14} weight="bold" className="mr-2" />
                Limpar tudo
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-none">
              <AlertDialogHeader>
                <AlertDialogTitle>Limpar histórico?</AlertDialogTitle>
                <AlertDialogDescription>
                  Todas as correções serão removidas permanentemente do banco.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-none" data-testid="clear-cancel">
                  Cancelar
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleClear}
                  data-testid="clear-confirm"
                  className="rounded-none bg-destructive text-destructive-foreground"
                >
                  Sim, limpar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <a
            href={exportUrl}
            data-testid="export-csv-button"
            className="inline-flex items-center gap-2 px-4 py-2 border border-foreground bg-foreground text-background text-xs font-mono-data uppercase tracking-widest hover:bg-primary hover:border-primary transition-colors"
          >
            <DownloadSimple size={14} weight="bold" />
            Export CSV
          </a>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 border border-border bg-card p-6">
        <div>
          <div className="label-eyebrow mb-2">turma</div>
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger data-testid="history-class-filter" className="rounded-none font-mono-data">
              <SelectValue placeholder="Todas as turmas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todas as turmas</SelectItem>
              {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="label-eyebrow mb-2">prova</div>
          <Select value={examId} onValueChange={setExamId} disabled={!classId}>
            <SelectTrigger data-testid="history-exam-filter" className="rounded-none font-mono-data">
              <SelectValue placeholder={classId ? "Todas as provas" : "Selecione uma turma"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todas as provas</SelectItem>
              {exams.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <div className="label-eyebrow mb-2">buscar por cpf</div>
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="search-cpf-input"
              placeholder="Ex: 12345678901"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 rounded-none font-mono-data border-border"
            />
          </div>
        </div>
      </div>

      {/* Handle sentinel value for "all" */}
      <ClearSentinel classId={classId} setClassId={setClassId} examId={examId} setExamId={setExamId} />

      {/* Tab switcher (only when exam selected) */}
      {examId && (
        <div className="flex items-center gap-2 mb-4">
          <button
            data-testid="tab-list"
            onClick={() => setView("list")}
            className={`px-4 py-2 text-xs font-mono-data uppercase tracking-widest border ${view === "list" ? "bg-foreground text-background border-foreground" : "border-border text-foreground hover:border-foreground"}`}
          >
            Lista
          </button>
          <button
            data-testid="tab-ranking"
            onClick={() => setView("ranking")}
            className={`px-4 py-2 text-xs font-mono-data uppercase tracking-widest border inline-flex items-center gap-2 ${view === "ranking" ? "bg-primary text-primary-foreground border-primary" : "border-border text-foreground hover:border-foreground"}`}
          >
            <Trophy size={14} weight="bold" /> Ranking
          </button>
        </div>
      )}

      {view === "ranking" && examId ? (
        <RankingPanel data={ranking} loading={rankLoading} />
      ) : (
        <ListPanel
          rows={rows}
          loading={loading}
          withExam={withExam}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}

/* Small helper that intercepts the shadcn "__all__" sentinel and clears the value */
function ClearSentinel({ classId, setClassId, examId, setExamId }) {
  useEffect(() => {
    if (classId === "__all__") setClassId("");
    // eslint-disable-next-line
  }, [classId]);
  useEffect(() => {
    if (examId === "__all__") setExamId("");
    // eslint-disable-next-line
  }, [examId]);
  return null;
}

function ListPanel({ rows, loading, withExam, onDelete }) {
  const cols = withExam
    ? "grid-cols-[1fr_140px_130px_90px_90px_110px_110px]"
    : "grid-cols-[1fr_180px_140px_110px_110px_110px]";
  return (
    <div className="border border-border bg-card">
      <div className={`grid ${cols} gap-4 px-6 py-3 border-b border-border label-eyebrow bg-muted/30`}>
        <div>Arquivo · Aluno</div>
        <div>CPF</div>
        <div>Data</div>
        {withExam ? <div>Acertos</div> : <div>Marcadas</div>}
        {withExam ? <div>Nota</div> : <div>Vazias</div>}
        {withExam && <div>Erros</div>}
        <div></div>
      </div>

      {loading ? (
        <div className="p-16 text-center label-eyebrow">carregando…</div>
      ) : rows.length === 0 ? (
        <div className="p-16 text-center">
          <div className="label-eyebrow">nenhum registro encontrado</div>
          <Link to="/" className="inline-block mt-4 text-primary text-sm font-mono-data hover:underline">
            → Envie seu primeiro cartão
          </Link>
        </div>
      ) : (
        rows.map((r, idx) => {
          const d = r.score_details;
          return (
            <div
              key={r.id}
              data-testid={`history-row-${idx}`}
              className={`grid ${cols} gap-4 px-6 py-4 border-b border-border last:border-b-0 items-center hover:bg-muted/20 transition-colors`}
            >
              <div className="min-w-0">
                <div className="text-sm truncate" title={r.filename}>{r.student_name || r.filename}</div>
                {r.student_name && (
                  <div className="text-[10px] font-mono-data text-muted-foreground truncate" title={r.filename}>
                    {r.filename}
                  </div>
                )}
              </div>
              <div className="font-mono-data font-semibold text-sm">{r.cpf}</div>
              <div className="font-mono-data text-xs text-muted-foreground">
                {new Date(r.created_at).toLocaleString("pt-BR")}
              </div>
              {withExam && d ? (
                <>
                  <div className="font-mono-data text-sm">
                    <span className="font-bold">{d.hits}</span>
                    <span className="text-muted-foreground text-xs">/{d.total_valid}</span>
                  </div>
                  <div className={`font-mono-data text-sm font-bold ${scoreTone(d.score)}`}>
                    {d.score.toFixed(1)}%
                  </div>
                  <div className="font-mono-data text-sm text-destructive">{d.misses}</div>
                </>
              ) : (
                <>
                  <div className="font-mono-data text-sm">{r.marked_objective}</div>
                  <div className="font-mono-data text-sm">{r.unanswered_objective}</div>
                </>
              )}
              <div className="flex items-center justify-end gap-3">
                <Link
                  to={`/correction/${r.id}`}
                  data-testid={`view-detail-btn-${idx}`}
                  className="inline-flex items-center gap-1 text-xs font-mono-data uppercase tracking-widest text-primary hover:underline"
                >
                  Ver <ArrowRight size={12} weight="bold" />
                </Link>
                <button
                  data-testid={`delete-btn-${idx}`}
                  onClick={() => onDelete(r.id)}
                  className="text-muted-foreground hover:text-destructive transition-colors"
                  title="Remover"
                >
                  <Trash size={14} weight="bold" />
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

function RankingPanel({ data, loading }) {
  if (loading) return <div className="p-16 text-center label-eyebrow border border-border">calculando ranking…</div>;
  if (!data || !data.results?.length) {
    return (
      <div className="p-16 text-center border border-border">
        <div className="label-eyebrow">nenhum aluno com correção nesta prova</div>
      </div>
    );
  }
  return (
    <div className="border border-border bg-card">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <div>
          <div className="label-eyebrow">ranking · {data.exam?.name}</div>
          <div className="font-display text-2xl font-black tracking-tight mt-1">
            {data.count} aluno(s) · {data.exam?.total_valid} questões válidas
            {data.exam?.annulled?.length ? ` · ${data.exam.annulled.length} anulada(s)` : ""}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-[70px_1fr_160px_100px_100px_100px_100px] gap-4 px-6 py-3 border-b border-border label-eyebrow bg-muted/30">
        <div>#</div>
        <div>Aluno</div>
        <div>CPF</div>
        <div>Acertos</div>
        <div>Erros</div>
        <div>Vazias</div>
        <div>Nota</div>
      </div>
      {data.results.map((r, idx) => {
        const highlight = r.rank <= 3
          ? "font-black text-primary"
          : "font-bold";
        return (
          <Link
            key={r.correction_id}
            to={`/correction/${r.correction_id}`}
            data-testid={`ranking-row-${idx}`}
            className="grid grid-cols-[70px_1fr_160px_100px_100px_100px_100px] gap-4 px-6 py-4 border-b border-border last:border-b-0 items-center hover:bg-muted/20 transition-colors"
          >
            <div className={`font-display text-2xl leading-none ${highlight}`}>
              {r.rank}º
            </div>
            <div className="min-w-0 truncate">
              <div className="text-sm font-semibold truncate">{r.student_name || "(sem cadastro)"}</div>
              <div className="text-[10px] font-mono-data text-muted-foreground truncate">{r.filename}</div>
            </div>
            <div className="font-mono-data text-sm">{r.cpf}</div>
            <div className="font-mono-data text-sm font-bold">{r.hits}</div>
            <div className="font-mono-data text-sm text-destructive">{r.misses}</div>
            <div className="font-mono-data text-sm text-muted-foreground">{r.blank_valid}</div>
            <div className={`font-mono-data text-lg font-black ${scoreTone(r.score)}`}>
              {r.score.toFixed(1)}%
            </div>
          </Link>
        );
      })}
    </div>
  );
}
